import { useStore } from '@nanostores/react';
import { memo, useCallback, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { ContainerWithBackButton } from '~/components/dialogs/settings/ContainerWithBackButton';
import { Alert } from '~/components/ui/Alert';
import { Button } from '~/components/ui/Button';
import { Input } from '~/components/ui/Input';
import { Link } from '~/components/ui/Link';
import { addDomainAlias } from '~/lib/.client/api/domains';
import { domainsStore } from '~/lib/stores/domains';
import { projectStore } from '~/lib/stores/project';
import { HELP_CENTER_DOMAINS } from '~/lib/urls';
import { withSpinner } from '~/utils/withSpinner';

export const AddDomainAlias = memo(({ onClose }: { onClose: VoidFunction }) => {
  const [alias, setAlias] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const projectId = useStore(projectStore.projectId);

  const onAliasChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setAlias(value);
    setError(null);
  }, []);

  const onSaveAlias = useCallback(async () => {
    if (!projectId) {
      setError('Project ID is required');
      return;
    }

    setIsSaving(true);

    await withSpinner({
      action: async () => {
        try {
          const data = await addDomainAlias(projectId, alias);

          domainsStore.domainEntries.set(data);
          domainsStore.setDomainAlert('Domain alias has been successfully added');

          domainsStore.resetStep();
        } catch (error) {
          setError(error instanceof Error ? error.message : 'Failed to add domain alias');
        }
      },
      onStateUpdate: setIsSaving,
    });
  }, [alias, onClose]);

  const handleInputKeyDown = useCallback(
    async (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && alias.trim()) {
        await onSaveAlias();
      }
    },
    [onSaveAlias, alias],
  );

  return (
    <ContainerWithBackButton className="gap-6" title="Add domain alias" onClose={onClose}>
      <p className="text-bolt-ds-textSecondary">
        Domain aliases are typically used to link additional vanity URLs to your project besides the primary domain.
      </p>

      {error && <Alert type="danger">{error}</Alert>}

      <Input
        inputClassName="bg-bolt-ds-surface"
        type="text"
        value={alias}
        label="Domain alias"
        onChange={onAliasChange}
        onKeyDown={handleInputKeyDown}
        placeholder="yourdomain.com"
      />

      <div className="flex justify-between items-center">
        <Link href={`${HELP_CENTER_DOMAINS}#add-a-domain-alias`} target="_blank" className="decoration-none text-sm">
          Learn more about domains
        </Link>
        <Button truncate={false} onClick={onSaveAlias} className="h-10 min-w-20" disabled={isSaving}>
          {isSaving ? (
            <>
              <span className="i-svg-spinners:90-ring-with-bg size-4 inline-block" />
              Saving...
            </>
          ) : (
            <>Save</>
          )}
        </Button>
      </div>
    </ContainerWithBackButton>
  );
});

AddDomainAlias.displayName = 'AddDomainAlias';
